import { Fluent, Widget, isArrayLike } from "@serenity-is/corelib";

declare module "@serenity-is/corelib" {
    export interface Fluent {
        firstChild<TElement extends HTMLElement = HTMLElement>(selector?: string): Fluent<TElement>;
        lastChild<TElement extends HTMLElement = HTMLElement>(selector?: string): Fluent<TElement>;
        width(): number;
        height(): number;
        innerHeight(): number;
        outerHeight(includeMargins?: boolean): number;
        innerWidth(): number;
        parents(selector?: string): HTMLElement[];
        prop(name: string): any;
        prop(name: string, value: any): this;
        position(): { top: number, left: number };
        offset(): { top: number, left: number };
        index(): number;
        isVisibleLike(): boolean;
        scrollLeft(): number;
        scrollLeft(value: number): this;
        scrollTop(): number;
        scrollTop(value: number): this;
        triggerLayout(): this;
    }
}

Fluent.prototype.isVisibleLike = function (this: Fluent<any>) {
    return !!(this.getNode() && Fluent.isVisibleLike(this.getNode()));
}

Fluent.prototype.triggerLayout = function (this: Fluent<any>) {
    if (this.length)
        Fluent.trigger(this.getNode(), "layout", { bubbles: false });
    return this;
}

Fluent.prototype.index = function (this: Fluent<any>) {
    if (!this.getNode())
        return -1;
    const parent = this.getNode()?.parentElement;
    if (!parent)
        return -1;
    const children = parent.children;
    for (let i = 0; i < children.length; i++) {
        if (children[i] === this.getNode()) {
            return i;
        }
    }
}

Fluent.prototype.parents = function (this: Fluent<any>, selector?: string): HTMLElement[] {
    const parents: HTMLElement[] = [];
    let current = this.getNode()?.parentElement;
    while (current) {
        if (!selector || current.matches(selector))
            parents.push(current);
        current = current.parentElement;
    }
    return parents;
}

Fluent.prototype.width = function (this: Fluent<any>, value?: number) {
    if (arguments.length > 0) {
        this.getNode()?.style.setProperty("width", value + "px");
        return this;
    }
    return this.getNode()?.getBoundingClientRect().width || 0;
};

Fluent.prototype.height = function (this: Fluent<any>, value?: number) {
    if (arguments.length > 0) {
        this.getNode()?.style.setProperty("height", value + "px");
        return this;
    }
    return this.getNode()?.getBoundingClientRect().height || 0;
}

Fluent.prototype.innerHeight = function (this: Fluent<any>) {
    return this.getNode()?.clientHeight || 0;
}

Fluent.prototype.outerHeight = function (this: Fluent<any>, includeMargins?: boolean) {
    var el = this.getNode();
    if (!el)
        return 0;
    var result = el.getBoundingClientRect().height;
    if (!includeMargins) {
        return result;
    }

    const style = getComputedStyle(el);
    return (
        result +
        parseFloat(style.marginTop) +
        parseFloat(style.marginBottom)
    );
}

Fluent.prototype.innerWidth = function (this: Fluent<any>) {
    return this.getNode()?.clientWidth || 0;
}

Fluent.prototype.offset = function (this: Fluent<any>) {
    return getElementOffset(this.getNode());
}

Fluent.prototype.position = function (this: Fluent<any>) {
    return getElementPosition(this.getNode());
}

Fluent.prototype.scrollLeft = function (this: Fluent<any>, value?: number) {
    if (arguments.length > 0) {
        if (this.length)
            this.getNode().scrollLeft = value;
        return this;
    }

    return this.getNode()?.scrollLeft || 0;
}

Fluent.prototype.scrollTop = function (this: Fluent<any>, value?: number) {
    if (arguments.length > 0) {
        if (this.length)
            this.getNode().scrollTop = value;
        return this;
    }

    return this.getNode()?.scrollTop || 0;
}

Fluent.prototype.prop = function (this: Fluent<any>, name: string, value: any) {
    if (arguments.length > 1) {
        if (this.length) {
            (this.getNode() as HTMLElement)[name] = name == "checked" ? !!value : value;
        }
        return this;
    }

    return this.getNode()?.[name];
}

Fluent.prototype.firstChild = function (this: Fluent<any>, selector?: string) {
    return Fluent(this.children(selector)[0]);
}

Fluent.prototype.lastChild = function (this: Fluent<any>, selector?: string) {
    return Fluent(this.children(selector).pop());
}

function getElementOffset(el: HTMLElement | ArrayLike<HTMLElement>) {
    if (isArrayLike(el)) {
        el = el[0];
    }
    if (!el)
        return null;

    const box = el.getBoundingClientRect();
    const docElem = document.documentElement;
    return {
        top: box.top + window.scrollY - docElem.clientTop,
        left: box.left + window.scrollX - docElem.clientLeft
    };
}

function getElementPosition(el: HTMLElement | ArrayLike<HTMLElement>) {
    if (isArrayLike(el)) {
        el = el[0];
    }
    if (!el)
        return null;

    const { top, left } = el.getBoundingClientRect();
    const { marginTop, marginLeft } = getComputedStyle(el);
    return {
        top: top - parseInt(marginTop, 10),
        left: left - parseInt(marginLeft, 10)
    };
}



